import React, { ComponentType, Suspense, useEffect } from "react";
import { Routes, Route, Navigate, Outlet, useSearchParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { Loader2 } from "lucide-react";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { AppDispatch } from "@/store/store";
import { getCurrentUser } from "@/store/slices/authSlice";
import { useMockAuth } from "@/mocks/mockData";
import { canUseAgent, FUNDER_STAFF, ORG_MANAGER, STAFF_USER } from "@/auth/capabilities";
import { DOCUMENT_SCOPES, type DocumentScope } from "@/services/docuseal";
import { MANAGED_USER_ROLES } from "@/services/users";

// Layouts
import MainLayout from "@/components/layout/MainLayout";
import AuthLayout from "@/components/layout/AuthLayout";

// Auth Pages
import Login from "@/pages/auth/Login";

// Broker Pages
import BrokerDashboard from "@/pages/broker/Dashboard";
import BrokerBorrowers from "@/pages/broker/Borrowers";
import BrokerAppDetail from "@/pages/broker/BrokerAppDetail";
import BorrowerForm from "@/pages/broker/BorrowerForm";
import BrokerProfile from "@/pages/broker/Profile";
import LinkConfirmPage from "@/agent/connections/LinkConfirmPage";
import BrokerSettings from "@pages/broker/Settings";

// Admin Pages
import AdminDashboard from "@/pages/admin/Dashboard";
import AdminAppDetail from "@/pages/admin/AdminAppDetail";
import AdminAnalytics from "@/pages/admin/Analytics";
import UserList from "@/pages/admin/users/UserList";
import UserInvite from "@/pages/admin/users/UserInvite";
import { asUserRole, USER_PAGES } from "@/pages/admin/users/userPages";
import AdminDocuments from "@/pages/admin/Documents";
import AdminDocumentNew from "@/pages/admin/DocumentNew";
import AdminDocumentEdit from "@/pages/admin/DocumentEdit";
import AdminBilling from "@/pages/admin/Billing";

// Common Pages
import Help from "@/pages/common/Help";
import DocuSignComplete from "@/pages/common/DocuSignComplete";
import PlaidConnectRedirect from "@/merchant-flow/runtime/PlaidConnectRedirect";
import AcceptInvitation from "@/pages/public/AcceptInvitation";
import ConfirmEmailChange from "@/pages/public/ConfirmEmailChange";
import OAuthConsent from "@/pages/oauth/OAuthConsent";
import Landing from "@/pages/Landing";
import PrivacyPolicy from "@/pages/legal/PrivacyPolicy";

// Components
import ProtectedRoute from "@/components/common/ProtectedRoute";
import ErrorView from "@/components/error/ErrorView";
import EjectErrorChrome from "@/components/error/EjectErrorChrome";
import {
  DEV_CRASH_PAGE_PATH,
  DEV_CRASH_SHELL_PATH,
  DevCrashPage,
} from "@/components/error/devCrashProbes";
import { User, UserRoles } from "@/types/auth";
import { lazyWithChunkReload } from "@/utils/lazyWithChunkReload";

// DEV only: the assistant's thread-switch spike and card fixtures (agent/spike); dead code in a build.
const ThreadSwitchSpike = import.meta.env.DEV
  ? React.lazy(() => import("@/agent/spike/ThreadSwitchSpike"))
  : null;
// DEV only: the chat's UI gallery (example data, signed out) and the live chat (signed in).
const AgentChatGallery = import.meta.env.DEV
  ? React.lazy(() => import("@/agent/chat/dev/AgentChatGallery"))
  : null;
const AgentChatLive = import.meta.env.DEV
  ? React.lazy(() => import("@/agent/chat/dev/AgentChatLive"))
  : null;
const AgentCardsFixture = import.meta.env.DEV
  ? React.lazy(() => import("@/agent/spike/AgentCardsFixture"))
  : null;

const MerchantFlowLayout = lazyWithChunkReload(
  () => import("@/merchant-flow/runtime/MerchantFlowLayout")
);
const FlowRuntime = lazyWithChunkReload(() => import("@/merchant-flow/runtime/FlowRuntime"));
const StaffFillPage = lazyWithChunkReload(() => import("@/staff-fill/StaffFillPage"));
const OAuthReturnPage = lazyWithChunkReload(
  () => import("@/merchant-flow/runtime/OAuthReturnPage")
);

const AssistantPage = lazyWithChunkReload(() => import("@/agent/assistant/AssistantPage"));

const notFoundPage = <ErrorView code={404} title="Page not found" />;

// Keyed per scope so moving between scopes never carries one scope's page state over.
const documentRoutes = (scope: DocumentScope) => (
  <>
    <Route path={scope.basePath} element={<AdminDocuments key={scope.key} scope={scope} />} />
    <Route
      path={`${scope.basePath}/new`}
      element={<AdminDocumentNew key={scope.key} scope={scope} />}
    />
    <Route
      path={`${scope.basePath}/:id/edit`}
      element={<AdminDocumentEdit key={scope.key} scope={scope} />}
    />
  </>
);

/** The former single Users page: its links land on the page of the role they named (brokers
 * for none or several), keeping the other filters. */
const LegacyUsersRedirect: React.FC<{ invite?: boolean }> = ({ invite = false }) => {
  const [searchParams] = useSearchParams();
  const { path } = USER_PAGES[asUserRole(searchParams.get("role")) ?? "broker"];
  const rest = new URLSearchParams(searchParams);
  rest.delete("role");
  const query = rest.toString();
  return <Navigate to={`${path}${invite ? "/new" : ""}${query ? `?${query}` : ""}`} replace />;
};

/** One list and one invite page per user type; the user editor is gone, so its links land on
 * the list. */
const userRoutes = MANAGED_USER_ROLES.map((role) => {
  const { path } = USER_PAGES[role];
  return (
    <React.Fragment key={role}>
      <Route path={path} element={<UserList key={role} role={role} />} />
      <Route path={`${path}/new`} element={<UserInvite key={role} role={role} />} />
      <Route path={`${path}/:id`} element={<Navigate to={path} replace />} />
    </React.Fragment>
  );
});

/** Not linked from navigation. To anyone but a system admin it is an unknown page (404). */
const SystemAdminRoute: React.FC = () => {
  const { isSystemAdmin } = useCurrentUser();
  return isSystemAdmin ? <Outlet /> : notFoundPage;
};

/** The assistant's page, for users who have the assistant; to anyone else an unknown page. */
const AssistantRoute: React.FC = () => {
  const { user } = useCurrentUser();
  if (!canUseAgent(user)) return notFoundPage;
  return (
    <Suspense fallback={null}>
      <AssistantPage />
    </Suspense>
  );
};

/** Billing nav + API are gated by Organization.billing_enabled in Django admin. */
const BillingEnabledRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useCurrentUser();
  return user?.billing_enabled ? <>{children}</> : notFoundPage;
};

const App: React.FC = () => {
  const { isAuthenticated, user, sessionChecked } = useCurrentUser();
  const dispatch = useDispatch<AppDispatch>();

  useEffect(() => {
    if (useMockAuth) return;
    if (!sessionChecked) {
      dispatch(getCurrentUser());
    }
  }, [sessionChecked, dispatch]);

  type RoleCompMap = Partial<Record<UserRoles, ComponentType>>;
  function withRoleGuard(user: User | null, roleCompMap: RoleCompMap) {
    return () => {
      if (!user) return <Navigate to="/login" />;
      const role = user.role;
      const Comp = roleCompMap[role];
      return Comp ? <Comp /> : <ErrorView />;
    };
  }

  const DashboardPage = withRoleGuard(user, {
    admin: AdminDashboard,
    admin_support: AdminDashboard,
    underwriter: AdminDashboard,
    broker: BrokerDashboard,
  });

  const AppDetailPage = withRoleGuard(user, {
    admin: AdminAppDetail,
    admin_support: AdminAppDetail,
    underwriter: AdminAppDetail,
    broker: BrokerAppDetail,
  });

  const protectedLayout = (
    <ProtectedRoute allowedRoles={[...STAFF_USER]}>
      <MainLayout />
    </ProtectedRoute>
  );

  const knownAppRoutes = (
    <>
      <Route path="/dashboard" element={<DashboardPage />} />
      <Route path="/applications" element={<BrokerBorrowers />} />
      <Route path="/applications/new" element={<BorrowerForm />} />
      <Route path="/applications/:id" element={<AppDetailPage />} />
      <Route path="/profile" element={<BrokerProfile />} />
      <Route path="/link/:channel" element={<LinkConfirmPage />} />
      <Route path="/help" element={<Help />} />
      <Route path="/assistant" element={<AssistantRoute />} />
      <Route path="/setting/:section?" element={<BrokerSettings />} />
      <Route path="/settings" element={<Navigate to="/setting" replace />} />
      <Route
        element={
          <ProtectedRoute allowedRoles={[...FUNDER_STAFF]}>
            <Outlet />
          </ProtectedRoute>
        }
      >
        <Route path="/analytics" element={<AdminAnalytics />} />
      </Route>
      <Route
        element={
          <ProtectedRoute allowedRoles={[...ORG_MANAGER]}>
            <Outlet />
          </ProtectedRoute>
        }
      >
        {userRoutes}
        <Route path="/users" element={<LegacyUsersRedirect />} />
        <Route path="/users/new" element={<LegacyUsersRedirect invite />} />
        <Route path="/users/:id" element={<Navigate to={USER_PAGES.broker.path} replace />} />
        <Route
          path="/billing"
          element={
            <BillingEnabledRoute>
              <AdminBilling />
            </BillingEnabledRoute>
          }
        />
        {documentRoutes(DOCUMENT_SCOPES.org)}
      </Route>
      <Route element={<SystemAdminRoute />}>{documentRoutes(DOCUMENT_SCOPES.system)}</Route>
    </>
  );

  const unknownPathFallback = !sessionChecked ? (
    <div className="flex h-screen items-center justify-center">
      <Loader2 className="h-10 w-10 animate-spin text-primary" aria-label="Loading" />
    </div>
  ) : (
    <EjectErrorChrome>{notFoundPage}</EjectErrorChrome>
  );

  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/docusign/complete" element={<DocuSignComplete />} />
      <Route
        element={
          <Suspense fallback={null}>
            <MerchantFlowLayout />
          </Suspense>
        }
      >
        <Route path="/flow/oauth-return" element={<OAuthReturnPage />} />
        <Route path="/flow/:token" element={<FlowRuntime />} />
        {/* A chat card's "Fill in and send" link: the merchant page's chrome, no staff sign-in. */}
        <Route path="/fill/:token" element={<StaffFillPage />} />
      </Route>
      <Route path="/plaid/connect/:token" element={<PlaidConnectRedirect />} />
      <Route path="/accept-invitation/:token" element={<AcceptInvitation />} />
      <Route path="/confirm-email/:token" element={<ConfirmEmailChange />} />
      <Route path="/privacy-policy" element={<PrivacyPolicy />} />
      {ThreadSwitchSpike && (
        <Route
          path="/dev/agent-switch-spike"
          element={
            <Suspense fallback={null}>
              <ThreadSwitchSpike />
            </Suspense>
          }
        />
      )}
      {AgentChatGallery && (
        <Route
          path="/dev/agent-chat"
          element={
            <Suspense fallback={null}>
              <AgentChatGallery />
            </Suspense>
          }
        />
      )}
      {AgentCardsFixture && (
        <Route
          path="/dev/agent-cards"
          element={
            <Suspense fallback={null}>
              <AgentCardsFixture />
            </Suspense>
          }
        />
      )}
      {/* An MCP app's sign-in: the user consents here, signed in as usual (no app chrome). */}
      <Route
        path="/oauth/consent/:request"
        element={
          <ProtectedRoute>
            <OAuthConsent />
          </ProtectedRoute>
        }
      />
      <Route element={<AuthLayout />}>
        <Route path="/login" element={<Login />} />
      </Route>

      {sessionChecked && isAuthenticated ? (
        <Route element={protectedLayout}>
          {knownAppRoutes}
          {import.meta.env.DEV && (
            <>
              <Route path={DEV_CRASH_PAGE_PATH} element={<DevCrashPage />} />
              <Route path={DEV_CRASH_SHELL_PATH} element={<React.Fragment />} />
              {AgentChatLive && (
                <Route
                  path="/dev/agent-chat/live"
                  element={
                    <Suspense fallback={null}>
                      <AgentChatLive />
                    </Suspense>
                  }
                />
              )}
            </>
          )}
          <Route path="*" element={notFoundPage} />
        </Route>
      ) : (
        <Route element={protectedLayout}>{knownAppRoutes}</Route>
      )}

      {/* Landing / Root */}
      <Route
        path="/"
        element={isAuthenticated ? <Navigate to="/dashboard" replace /> : <Landing />}
      />
      <Route path="*" element={unknownPathFallback} />
    </Routes>
  );
};

export default App;
